/**
 * Main Application Component
 *
 * The root component that manages application state and
 * coordinates the map, sidebar, safety components, and data fetching.
 */

import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import Map, { MapBounds } from './components/Map';
import Sidebar from './components/Sidebar';
import SafetyBanner, { hasDismissedSafetyBanner, dismissSafetyBanner } from './components/SafetyBanner';
import CookieBanner from './components/CookieBanner';
import OfflineBanner from './components/OfflineBanner';
import MobileSidebar from './components/MobileSidebar';
import DisclaimerModal, { hasAcceptedDisclaimer } from './components/DisclaimerModal';
import { CompassIcon } from './components/icons';
import ErrorBoundary from './components/ErrorBoundary';
import {
  trackModalOpen,
  trackModalClose,
  trackMobileViewToggle,
  trackFireEventSelect,
  trackLayerToggle,
  trackAreaSelect,
  trackDataRefresh,
  trackDisclaimerAccept,
  trackOnboardingComplete,
} from './utils/analytics';

// Onboarding persistence - inline to avoid importing the whole modal
const ONBOARDING_KEY = 'staysafe-onboarding-completed';
const ONBOARDING_VERSION = '1.0';
const ONBOARDING_PILL_DISMISSED_KEY = 'staysafe-onboarding-pill-dismissed';
function hasCompletedOnboarding(): boolean {
  try {
    const stored = localStorage.getItem(ONBOARDING_KEY);
    if (!stored) return false;
    const data = JSON.parse(stored);
    return data.version === ONBOARDING_VERSION;
  } catch {
    return false;
  }
}
function hasDismissedOnboardingPill(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_PILL_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * P1-16: return the visit count without incrementing. Read-only mirror of
 * the SUPPORTER_SESSION_KEY counter so the onboarding pill can gate on
 * session ≥ 2 without double-incrementing the counter. The supporter
 * effect later in the component runs incrementSessionCount() once per
 * mount; this read fires before that, so the FIRST visit reads 0, the
 * second visit reads 1 (post-increment from previous session), etc.
 */
function getSessionCount(): number {
  try {
    return parseInt(localStorage.getItem(SUPPORTER_SESSION_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

// Supporter modal session tracking - show on 3rd session for non-supporters
const SUPPORTER_SESSION_KEY = 'staysafe-session-count';
const SUPPORTER_MODAL_SHOWN_KEY = 'staysafe-supporter-modal-shown';
function incrementSessionCount(): number {
  try {
    const count = parseInt(localStorage.getItem(SUPPORTER_SESSION_KEY) || '0', 10) + 1;
    localStorage.setItem(SUPPORTER_SESSION_KEY, count.toString());
    return count;
  } catch {
    return 1;
  }
}
function hasSupporterModalBeenShown(): boolean {
  try {
    return localStorage.getItem(SUPPORTER_MODAL_SHOWN_KEY) === 'true';
  } catch {
    return false;
  }
}
function markSupporterModalShown(): void {
  try {
    localStorage.setItem(SUPPORTER_MODAL_SHOWN_KEY, 'true');
  } catch {
    // Ignore storage errors
  }
}
import OfficialLinks from './components/OfficialLinks';
import HealthDetailPanel from './components/HealthDetailPanel';
import UserMenu from './components/UserMenu';
import NotificationBadge from './components/NotificationBadge';
import ToastContainer from './components/ToastContainer';
import { MapViewProvider, useMapView } from './contexts/MapViewContext';

// Lazy load modals that are not immediately visible to reduce initial bundle size
const AuthModal = lazy(() => import('./components/AuthModal'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const ProfileModal = lazy(() => import('./components/ProfileModal'));
const RemovalRequestModal = lazy(() => import('./components/RemovalRequestModal'));
const AboutModal = lazy(() => import('./components/AboutModal'));
const NotificationPage = lazy(() => import('./components/NotificationPage'));
const OnboardingModal = lazy(() => import('./components/OnboardingModal'));
const SupporterModal = lazy(() => import('./components/SupporterModal'));
const AddMediaInfoModal = lazy(() => import('./components/AddMediaInfoModal'));

// Detail panels — only load when the user selects an event. Saves ~30 KB
// from the initial bundle for users who never click into a fire detail.
const FireDetailPanel = lazy(() => import('./components/FireDetailPanel'));
const MobileFireDetailPanel = lazy(() => import('./components/MobileFireDetailPanel'));

// Lazy load 3D map component (large bundle with Cesium)
const Map3D = lazy(() => import('./components/Map3D/Map3D'));
import {
  fetchRecentEvents,
  uploadPhoto,
  fetchPhotos,
  WatchZone,
  fetchEventsCached,
  isEventsCacheAvailable
} from './api/client';
import { useToast } from './hooks/useToast';
import { UnifiedEvent, AreaPreset, GeoPhoto, EventType } from './types/events';
import LocationSearch from './components/LocationSearch';
import { LayerInfoButton } from './components/LayerInfoModal';
import { useAuth } from './contexts/AuthContext';
import { useWatchZones } from './contexts/WatchZoneContext';
import { SupporterProvider, useSupporter } from './contexts/SupporterContext';
import { usePushSubscription } from './hooks/usePushSubscription';
import { loadLayerSettings, saveLayerSettings, LayerSettings } from './hooks/useLayerSettings';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
// exifr is lazy loaded when needed to reduce initial bundle size (~300KB)

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Default configuration.
 */
// Default to center of Australia - shows entire continent when geolocation unavailable
const DEFAULT_AREA: AreaPreset = { name: 'Australia', lat: -25.5, lon: 134.5, defaultRadiusKm: 3000 };
const MINUTES_BACK = 1440; // 24 hours - used as stale data filter
const REFRESH_INTERVAL_MS = 60000; // 1 minute

/**
 * Inner App component that uses MapViewContext.
 * Separated to allow MapViewProvider wrapper.
 */
function AppContent() {
  // Map view state (2D/3D toggle)
  const { viewMode, setViewMode, enterFocused3DMode, focusedFire, exitFocused3DMode } = useMapView();

  // Auth state
  const { getAccessToken, isAuthenticated } = useAuth();
  const { isSupporter, isLoading: supporterLoading } = useSupporter();

  // Push notification subscription (handles both Web Push and FCM token registration)
  // This is initialized at app level to capture FCM tokens from Android TWA early
  const { isTWA, fcmRegistered } = usePushSubscription();

  // Debug logging for TWA/FCM status (only in dev)
  useEffect(() => {
    if (import.meta.env.DEV && isTWA) {
      console.log('[App] Running in Android TWA, FCM registered:', fcmRegistered);
    }
  }, [isTWA, fcmRegistered]);

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showNotificationPage, setShowNotificationPage] = useState(false);
  const [showSupporterModal, setShowSupporterModal] = useState(false);
  const [removalRequest, setRemovalRequest] = useState<{ photoId: string; fileName: string } | null>(null);

  // Show supporter modal on 3rd session for authenticated non-supporters
  const sessionCountRef = useRef<number | null>(null);
  useEffect(() => {
    // Only run once per session
    if (sessionCountRef.current !== null) return;
    sessionCountRef.current = incrementSessionCount();
  }, []);

  // Trigger supporter modal on 3rd session
  useEffect(() => {
    if (
      sessionCountRef.current === 3 &&
      isAuthenticated &&
      !supporterLoading &&
      !isSupporter &&
      !hasSupporterModalBeenShown()
    ) {
      // Small delay to let the app settle
      const timer = setTimeout(() => {
        setShowSupporterModal(true);
        markSupporterModalShown();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, supporterLoading, isSupporter]);

  // Watch zone state (persistent zones)
  const { isEditorOpen, zones } = useWatchZones();
  // Editor preview state for showing circle on map while editing
  const [editorPreviewCenter, setEditorPreviewCenter] = useState<{ lat: number; lon: number } | null>(null);
  const [editorPreviewRadius, setEditorPreviewRadius] = useState<number>(25);
  // Drawing state for polygon zones
  const [isDrawingZone, setIsDrawingZone] = useState(false);
  const [zoneShapeType, setZoneShapeType] = useState<'circle' | 'polygon'>('circle');
  // Zone visibility state (which zones are shown on map) - default all visible
  const [visibleZoneIds, setVisibleZoneIds] = useState<Set<string>>(new Set());

  // Initialize visible zones when zones load (all visible by default)
  useEffect(() => {
    if (zones.length > 0) {
      setVisibleZoneIds((prev) => {
        // Add any new zones that aren't already tracked
        const newSet = new Set(prev);
        zones.forEach((zone) => {
          if (!prev.has(zone.zoneId)) {
            newSet.add(zone.zoneId);
          }
        });
        // Remove any zones that no longer exist
        prev.forEach((id) => {
          if (!zones.find((z) => z.zoneId === id)) {
            newSet.delete(id);
          }
        });
        return newSet;
      });
    }
  }, [zones]);

  // Toast notifications
  const { toasts, showToast, dismissToast } = useToast();

  // Safety state
  const [showDisclaimer, setShowDisclaimer] = useState(!hasAcceptedDisclaimer());
  const [showOnboarding, setShowOnboarding] = useState(false); // Modal — only opened on user action (pill click or About modal)
  // Non-blocking "Take a tour" pill in the header. Shown from the SECOND
  // visit onwards so first-time emergency users see the map immediately
  // (P1-16): the 👋 pill competing for attention against a wall of red
  // markers during a live fire is the wrong first impression. After the
  // first visit, the supporter session counter has incremented to ≥1 so
  // the gate `getSessionCount() >= 1` is true. The disclaimer-accept
  // path also flips the counter via the supporter session effect.
  const [showOnboardingPill, setShowOnboardingPill] = useState<boolean>(
    () =>
      !hasCompletedOnboarding() &&
      !hasDismissedOnboardingPill() &&
      getSessionCount() >= 1
  );
  const [showBanner, setShowBanner] = useState(!hasDismissedSafetyBanner()); // Permanently dismissible
  const [isOfficialLinksOpen, setIsOfficialLinksOpen] = useState(false);

  // Sidebar resize state
  const [sidebarWidth, setSidebarWidth] = useState(450);
  const [isResizing, setIsResizing] = useState(false);

  // Map display state - persisted to localStorage so users don't have to reconfigure each visit
  // Load saved settings once on mount (lazy initializer)
  const [savedLayerSettings] = useState<LayerSettings>(() => loadLayerSettings());
  const [showWindLayer, setShowWindLayer] = useState(savedLayerSettings.showWindLayer);
  const [showAircraftLayer, setShowAircraftLayer] = useState(savedLayerSettings.showAircraftLayer);
  const [showFireBoundaries, setShowFireBoundaries] = useState(savedLayerSettings.showFireBoundaries);
  const [showWarningAreas, setShowWarningAreas] = useState(savedLayerSettings.showWarningAreas);
  const [showHotspots, setShowHotspots] = useState(savedLayerSettings.showHotspots);
  const [showRoadClosures, setShowRoadClosures] = useState(savedLayerSettings.showRoadClosures);
  const [showPowerOutages, setShowPowerOutages] = useState(savedLayerSettings.showPowerOutages);
  const [showSmoke, setShowSmoke] = useState(savedLayerSettings.showSmoke);
  const [showAirQuality, setShowAirQuality] = useState(savedLayerSettings.showAirQuality);
  const [showCityTemperatures, setShowCityTemperatures] = useState(savedLayerSettings.showCityTemperatures);
  const [showThunderstormWarnings, setShowThunderstormWarnings] = useState(savedLayerSettings.showThunderstormWarnings);
  const [showLightningRisk, setShowLightningRisk] = useState(savedLayerSettings.showLightningRisk);
  const [showPlannedBurns, setShowPlannedBurns] = useState(savedLayerSettings.showPlannedBurns);
  const [showRadioMenu, setShowRadioMenu] = useState(false);
  const [showLayersMenu, setShowLayersMenu] = useState(false); // Layers dropdown for mobile
  const [showPhotosLayer, setShowPhotosLayer] = useState(savedLayerSettings.showPhotosLayer);
  const [isLocating, setIsLocating] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);

  // Persist layer settings to localStorage whenever they change
  useEffect(() => {
    saveLayerSettings({
      showWindLayer,
      showAircraftLayer,
      showFireBoundaries,
      showWarningAreas,
      showHotspots,
      showRoadClosures,
      showPowerOutages,
      showSmoke,
      showAirQuality,
      showCityTemperatures,
      showThunderstormWarnings,
      showLightningRisk,
      showPhotosLayer,
      showPlannedBurns,
    });
  }, [
    showWindLayer,
    showAircraftLayer,
    showFireBoundaries,
    showWarningAreas,
    showHotspots,
    showRoadClosures,
    showPowerOutages,
    showSmoke,
    showAirQuality,
    showCityTemperatures,
    showThunderstormWarnings,
    showLightningRisk,
    showPhotosLayer,
    showPlannedBurns,
  ]);

  // Mobile bottom-sheet position: 'map' is the peek state (just the bar
  // visible, map dominant), 'half' shows ~50vh of the sheet over the map
  // (top fires reachable without losing geographic context), 'list' is the
  // fully expanded sheet. Mirrors the snap-point pattern used by Google
  // and Apple Maps. Touch handlers snap to the closest of these three at
  // drag-end; the bar tap cycles peek → half → list → peek.
  const [mobileView, setMobileView] = useState<'map' | 'half' | 'list'>('map');

  // One-shot request to force the MobileSidebar onto a particular sub-view
  // (the fire list vs the watch zone manager). Set by handlers like
  // handleCreateWatchZone that need to drop the user straight into the
  // zones tab; cleared shortly after so the user's manual navigation
  // sticks. Null means "let MobileSidebar manage its own view."
  const [requestedSidebarView, setRequestedSidebarView] = useState<'list' | 'zones' | null>(null);

  // Landscape-only: lets the user slide the right-rail off-screen to get
  // the full map back. Persisted across sessions so a user who prefers the
  // map-dominant view doesn't have to re-collapse on every visit.
  const [landscapeRailCollapsed, setLandscapeRailCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('firewatch:landscape-rail-collapsed') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('firewatch:landscape-rail-collapsed', String(landscapeRailCollapsed));
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  }, [landscapeRailCollapsed]);

  // Used by the bottom-sheet drag handlers to skip the spring animation for
  // users with `prefers-reduced-motion: reduce`. CSS-defined transitions are
  // already disabled globally in App.css, but we set transitions imperatively
  // on the sheet from JS, which bypasses that rule.
  const reduceMotion = usePrefersReducedMotion();
  const sheetTransition = reduceMotion
    ? 'none'
    : 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';

  // Track viewport width so we can render the mobile-optimised MobileSidebar
  // instead of the desktop Sidebar at <=768px. Re-evaluates on resize.
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Track previous editor state to detect when it opens/closes
  const prevEditorOpenRef = useRef(false);

  // ICAO24 of an aircraft to auto-follow in 3D cockpit mode after a 2D
  // → 3D transition. Set by the "Ride Along" button on the 2D aircraft
  // popup; consumed by Map3D once Cesium is ready, then cleared.
  const [pendingAircraftFollow, setPendingAircraftFollow] = useState<string | null>(null);

  // Mobile bottom sheet drag refs
  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const sheetDragState = useRef({
    isDragging: false,
    startY: 0,
    startTranslateY: 0,
    currentTranslateY: 0,
  });

  // Height of the visible bar in the peek state. Must stay in sync with
  // --mobile-bottom-sheet-handle in App.css and the bar's own min-height.
  const SHEET_HANDLE_HEIGHT = 64;
  // translateY (as a fraction of viewport height) for the half-snap.
  // The sheet is 85vh tall with its bottom anchored to the viewport
  // bottom, so translateY(35vh) leaves 50vh of sheet visible — the top
  // half of the viewport stays as usable map. Matches Google/Apple Maps'
  // default mid-anchor. Keep this in sync with .sidebar-wrapper.sheet-half
  // in App.css.
  const SHEET_HALF_VIEWPORT_FRACTION = 0.35;

  // Compute the translateY (in px) for a given snap state. Centralised so
  // touch handlers, the sync useEffect, and the tap handler all agree on
  // exactly where each snap point sits.
  const sheetTranslateYFor = useCallback(
    (view: 'map' | 'half' | 'list', sheet: HTMLElement): number => {
      if (view === 'list') return 0;
      if (view === 'half') {
        return Math.max(0, window.innerHeight * SHEET_HALF_VIEWPORT_FRACTION);
      }
      // peek
      return sheet.offsetHeight - SHEET_HANDLE_HEIGHT;
    },
    []
  );

  // Apply a settled snap target to the sheet. Uses the same brittleness-
  // dodge as the original code: when settling to peek, we clear the inline
  // transform and let the CSS `transform: translateY(calc(100% - 64px))`
  // rule resolve at layout time (immune to post-pull-to-refresh height
  // glitches). Half + list use inline transforms because there's no
  // layout-time equivalent for the half-position px math.
  const applySheetSnap = useCallback(
    (view: 'map' | 'half' | 'list', sheet: HTMLElement) => {
      sheet.style.transition = sheetTransition;
      if (view === 'map') {
        sheet.style.transform = '';
      } else if (view === 'half') {
        sheet.style.transform = `translateY(${sheetTranslateYFor('half', sheet)}px)`;
      } else {
        sheet.style.transform = 'translateY(0)';
      }
    },
    [sheetTransition, sheetTranslateYFor]
  );

  const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
    const sheet = bottomSheetRef.current;
    if (!sheet) return;
    const state = sheetDragState.current;
    state.isDragging = true;
    state.startY = e.touches[0].clientY;
    state.startTranslateY = sheetTranslateYFor(mobileView, sheet);
    state.currentTranslateY = state.startTranslateY;
    sheet.style.transition = 'none';
  }, [mobileView, sheetTranslateYFor]);

  const handleSheetTouchMove = useCallback((e: React.TouchEvent) => {
    const sheet = bottomSheetRef.current;
    const state = sheetDragState.current;
    if (!state.isDragging || !sheet) return;
    e.preventDefault();

    const deltaY = e.touches[0].clientY - state.startY;
    // Clamp between 0 (fully open) and the peek position (bar only).
    const maxTranslate = sheet.offsetHeight - SHEET_HANDLE_HEIGHT;
    const newTranslateY = Math.max(0, Math.min(maxTranslate, state.startTranslateY + deltaY));
    state.currentTranslateY = newTranslateY;
    sheet.style.transform = `translateY(${newTranslateY}px)`;
  }, []);

  const handleSheetTouchEnd = useCallback(() => {
    const sheet = bottomSheetRef.current;
    const state = sheetDragState.current;
    if (!state.isDragging || !sheet) return;
    state.isDragging = false;

    // Find the closest snap target by absolute distance. Skip velocity-based
    // overrides for now — distance snapping is predictable and matches what
    // users expect from Maps apps on the same hardware.
    const candidates: Array<'map' | 'half' | 'list'> = ['map', 'half', 'list'];
    const targets = candidates.map(v => ({
      view: v,
      y: sheetTranslateYFor(v, sheet),
    }));
    const nearest = targets.reduce((a, b) =>
      Math.abs(a.y - state.currentTranslateY) <= Math.abs(b.y - state.currentTranslateY) ? a : b
    );

    if (nearest.view !== mobileView) {
      trackMobileViewToggle(nearest.view === 'list' ? 'list' : 'map');
      setMobileView(nearest.view);
    }
    applySheetSnap(nearest.view, sheet);
  }, [mobileView, sheetTranslateYFor, applySheetSnap]);

  // Helper to create tracked layer toggle handlers
  const createTrackedLayerToggle = useCallback(
    (layerName: string, currentState: boolean, setter: (value: boolean) => void) => {
      return () => {
        const newState = !currentState;
        trackLayerToggle(layerName, newState);
        setter(newState);
      };
    },
    []
  );

  // Auto-switch to map view on mobile when zone editor opens
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const justOpened = isEditorOpen && !prevEditorOpenRef.current;
    const justClosed = !isEditorOpen && prevEditorOpenRef.current;

    if (justOpened && isMobile && mobileView !== 'map') {
      // Editor just opened - force peek so the map is fully visible
      setMobileView('map');
    } else if (justClosed && isMobile) {
      // Editor just closed - return to list view
      setMobileView('list');
    }

    prevEditorOpenRef.current = isEditorOpen;
  }, [isEditorOpen, mobileView]);

  // Sync bottom sheet position when mobileView changes programmatically.
  // applySheetSnap encapsulates the per-state transform choice (including
  // the peek-state CSS-rule fallback that survives post-pull-to-refresh
  // height glitches).
  useEffect(() => {
    const sheet = bottomSheetRef.current;
    if (!sheet || window.innerWidth > 768) return;
    applySheetSnap(mobileView, sheet);
  }, [mobileView, applySheetSnap]);

  // Data state
  const [events, setEvents] = useState<UnifiedEvent[]>([]);
  const [selectedArea, setSelectedArea] = useState<AreaPreset>(DEFAULT_AREA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // User's initial location (set once on session start)
  const [userLocation, setUserLocation] = useState<{ lat: number; lon: number } | null>(null);
  const hasRequestedLocation = useRef(false);
  const [recenterTrigger, setRecenterTrigger] = useState(0);
  // Track whether we've proactively asked for notification permission this
  // session — avoids re-prompting on every render once the user has decided.
  const hasRequestedNotificationPermission = useRef(false);

  /**
   * Calculate distance between two coordinates using Haversine formula.
   * Returns distance in kilometers.
   */
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  /**
   * Enrich events with distance from user's location.
   */
  const enrichEventsWithDistance = (
    eventsToEnrich: UnifiedEvent[],
    location: { lat: number; lon: number } | null
  ): UnifiedEvent[] => {
    if (!location) return eventsToEnrich;

    return eventsToEnrich.map(event => ({
      ...event,
      _distanceKm: calculateDistance(
        location.lat,
        location.lon,
        event.location.lat,
        event.location.lon
      ),
    }));
  };

  // Selected event state (for detail panel and map focus)
  const [selectedEvent, setSelectedEvent] = useState<UnifiedEvent | null>(null);

  // Photo upload state
  const [photos, setPhotos] = useState<GeoPhoto[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  // Explainer modal shown before the OS file picker opens.
  const [showAddMediaModal, setShowAddMediaModal] = useState(false);

  /**
   * Severity priority for sorting (higher = more severe)
   */
  const SEVERITY_PRIORITY: Record<string, number> = {
    'EMERGENCY': 4,
    'WATCH_AND_ACT': 3,
    'ADVICE': 2,
    'NOT_APPLICABLE': 1,
  };

  /**
   * Filter events to only show those visible on the current map viewport,
   * sorted by severity (most severe first).
   */
  // Layer-filtered events for the MAP (no bounds filter — the map needs
  // events outside the current viewport for clustering / zoom-out / drag).
  // The planned-burns toggle gets applied here so it actually affects the
  // markers on the map, not just the list. Backend tags isPlannedBurn at
  // ingestion across NSW/VIC/QLD/SA/WA.
  const eventsForMap = useMemo(() => {
    return showPlannedBurns
      ? events
      : events.filter((e) => !(e.data?.isPlannedBurn === true));
  }, [events, showPlannedBurns]);

  const visibleEvents = useMemo(() => {
    if (!mapBounds) return eventsForMap;

    // Filter to events within current map bounds
    const filtered = eventsForMap.filter((event) => {
      const { lat, lon } = event.location;
      return (
        lat >= mapBounds.south &&
        lat <= mapBounds.north &&
        lon >= mapBounds.west &&
        lon <= mapBounds.east
      );
    });

    // Sort by severity (most severe first)
    return filtered.sort((a, b) => {
      const severityA = SEVERITY_PRIORITY[a.data?.severity as string] || 0;
      const severityB = SEVERITY_PRIORITY[b.data?.severity as string] || 0;
      return severityB - severityA;
    });
  }, [eventsForMap, mapBounds]);

  // Counts shown on the mobile bottom-sheet drag handle. `total` matches the
  // drawer's fire count exactly (all FIRE_INCIDENT + BURN_AREA, regardless of
  // severity) so users don't see two different numbers. Emergency / watch-act
  // counts are only used to add an extra urgency badge — Advice fires are the
  // baseline and don't need a separate coloured tag.
  const sheetSummary = useMemo(() => {
    let emergency = 0, watchAct = 0, total = 0;
    for (const e of visibleEvents) {
      if (e.type !== 'FIRE_INCIDENT' && e.type !== 'BURN_AREA') continue;
      total++;
      const sev = e.data?.severity as string | undefined;
      if (sev === 'EMERGENCY') emergency++;
      else if (sev === 'WATCH_AND_ACT') watchAct++;
    }
    return { emergency, watchAct, total };
  }, [visibleEvents]);

  // Event cache for stale-while-revalidate pattern
  const eventsCache = useRef<{
    data: UnifiedEvent[];
    timestamp: number;
    params: string;
  } | null>(null);
  const CACHE_TTL_MS = 30000; // 30 seconds - skip refetch if cache is fresh

  /**
   * Fetch events from the API with stale-while-revalidate caching.
   * Shows all active incidents (filtered by status, not time window).
   * Always fetches a large radius - watch zone only affects monitoring/alerts.
   */
  const loadEvents = useCallback(async (forceRefresh = false) => {
    const cacheKey = `${selectedArea.lat},${selectedArea.lon}`;
    const now = Date.now();

    // Check if cache is fresh enough (stale-while-revalidate)
    if (!forceRefresh && eventsCache.current) {
      const { data, timestamp, params } = eventsCache.current;
      const age = now - timestamp;

      // If cache is fresh and params match, skip the fetch entirely
      if (age < CACHE_TTL_MS && params === cacheKey) {
        return;
      }

      // If cache exists but is stale, use it while revalidating in background
      if (params === cacheKey && data.length > 0) {
        // Don't show loading state for background revalidation
        // Fetch in background without blocking
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      let fetchedEvents: UnifiedEvent[];

      // Try CloudFront cache first (much cheaper at scale)
      if (isEventsCacheAvailable()) {
        try {
          const cached = await fetchEventsCached();
          if (cached && cached.events && cached.events.length > 0) {
            // Map cache format to UnifiedEvent
            fetchedEvents = cached.events.map(e => ({
              id: e.id,
              type: e.type as EventType,
              location: e.location,
              region: e.region,
              observedAt: e.observedAt,
              confidence: typeof e.confidence === 'string' ? parseFloat(e.confidence) || 0 : e.confidence,
              title: e.title,
              description: e.description,
              data: e.data
            }));
          } else {
            // Cache empty or failed, fall back to API
            const response = await fetchRecentEvents({
              lat: selectedArea.lat,
              lon: selectedArea.lon,
              radiusKm: 5000,
              minutesBack: MINUTES_BACK,
            });
            fetchedEvents = response.events;
          }
        } catch (cacheError) {
          // Cache fetch failed, fall back to API
          console.warn('Cache fetch failed, falling back to API:', cacheError);
          const response = await fetchRecentEvents({
            lat: selectedArea.lat,
            lon: selectedArea.lon,
            radiusKm: 5000,
            minutesBack: MINUTES_BACK,
          });
          fetchedEvents = response.events;
        }
      } else {
        // No cache URL configured, use API directly
        const response = await fetchRecentEvents({
          lat: selectedArea.lat,
          lon: selectedArea.lon,
          radiusKm: 5000,
          minutesBack: MINUTES_BACK,
        });
        fetchedEvents = response.events;
      }

      // Update cache with raw events
      eventsCache.current = {
        data: fetchedEvents,
        timestamp: now,
        params: cacheKey,
      };

      // Enrich events with distance from user's location
      const enrichedEvents = enrichEventsWithDistance(fetchedEvents, userLocation);
      setEvents(enrichedEvents);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch events:', err);
      // If we have cached data, don't show error
      if (eventsCache.current?.data.length) {
        console.log('Using cached events after fetch error');
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to load events. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedArea, userLocation]);

  // Re-enrich events with distance when user location becomes available
  useEffect(() => {
    if (userLocation && events.length > 0) {
      setEvents(prevEvents => enrichEventsWithDistance(prevEvents, userLocation));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]); // Only re-run when userLocation changes, not when events change

  // Load events on mount and when filters change (only after disclaimer accepted)
  useEffect(() => {
    if (!showDisclaimer) {
      loadEvents();
    }
  }, [loadEvents, showDisclaimer]);

  // Set up auto-refresh (only after disclaimer accepted)
  useEffect(() => {
    if (showDisclaimer) return;

    const interval = setInterval(() => {
      loadEvents();
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadEvents, showDisclaimer]);

  // Request user location on initial page load to center the map
  useEffect(() => {
    // Only run once per session
    if (hasRequestedLocation.current) return;
    hasRequestedLocation.current = true;

    // Check if geolocation is available
    if (!navigator.geolocation) return;

    // Request location with high accuracy but short timeout
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lon: longitude });

        // Update the selected area to user's location
        const myLocationArea: AreaPreset = {
          name: 'My Location',
          lat: latitude,
          lon: longitude,
          defaultRadiusKm: 100,
        };
        setSelectedArea(myLocationArea);
      },
      () => {
        // Silently fail - just use default location (All Australia)
        // User can manually use "My Location" button if needed
      },
      {
        enableHighAccuracy: false, // Use IP-based location for speed
        timeout: 5000, // 5 second timeout
        maximumAge: 300000, // Accept cached position up to 5 minutes old
      }
    );
  }, []);

  // Proactively ask for notification permission once the user signs in.
  // Without this, watch zone alerts can't show on Android 13+ (which
  // requires runtime POST_NOTIFICATIONS permission). Inside the TWA Chrome
  // delegates `Notification.requestPermission()` to the Android system
  // prompt automatically. We only ask if the current state is `default`
  // — re-prompting after a denial is annoying, and `granted` already works.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (hasRequestedNotificationPermission.current) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'default') return;

    hasRequestedNotificationPermission.current = true;
    // Slight delay so the prompt doesn't collide with the post-sign-in
    // render (auth modal closing, sidebar updating, etc.) — feels less
    // jarring than firing the system dialog mid-transition.
    const t = setTimeout(() => {
      Notification.requestPermission().catch(() => { /* silently ignore */ });
    }, 800);
    return () => clearTimeout(t);
  }, [isAuthenticated]);

  /**
   * Handle disclaimer acceptance.
   * Shows onboarding modal if user hasn't completed it before.
   */
  const handleDisclaimerAccept = () => {
    trackDisclaimerAccept();
    setShowDisclaimer(false);
    // We deliberately don't auto-open the OnboardingModal here. For an
    // emergency tool, blocking the map with a wizard on first paint is
    // wrong — users landing during a fire want the map immediately. The
    // header instead shows a small "Take a tour" pill (showOnboardingPill)
    // they can tap when ready, and the same tour is reachable from the
    // About modal.
  };

  /**
   * Handle onboarding completion or skip. Also clears the pill so it
   * doesn't reappear after they finish.
   */
  const handleOnboardingClose = (skipped: boolean = false) => {
    trackOnboardingComplete(skipped);
    trackModalClose('onboarding', skipped ? 'skipped' : 'completed');
    setShowOnboarding(false);
    setShowOnboardingPill(false);
  };

  const handleOnboardingPillDismiss = () => {
    try { localStorage.setItem(ONBOARDING_PILL_DISMISSED_KEY, 'true'); } catch {}
    setShowOnboardingPill(false);
  };

  const handleOnboardingPillClick = () => {
    trackModalOpen('onboarding');
    setShowOnboarding(true);
  };

  /**
   * Handle area selection change.
   * PERFORMANCE: Memoized to prevent child re-renders.
   */
  const handleAreaChange = useCallback((area: AreaPreset) => {
    trackAreaSelect(area.name);
    setSelectedArea(area);
  }, []);

  /**
   * Handle "My Location" button - uses cached location if available for instant response,
   * otherwise falls back to browser geolocation API.
   */
  const handleMyLocation = useCallback(() => {
    // If we already have the user's location, fly there instantly
    if (userLocation) {
      const myLocationArea: AreaPreset = {
        id: 'my-location',
        name: 'My Location',
        lat: userLocation.lat,
        lon: userLocation.lon,
        defaultRadiusKm: 50,
      };
      setSelectedArea(myLocationArea);
      setRecenterTrigger(t => t + 1);
      return;
    }

    if (!navigator.geolocation) {
      showToast({ type: 'error', title: 'Not Supported', message: 'Geolocation is not supported by your browser' });
      return;
    }

    setIsLocating(true);

    // Pre-flight permission check via the Permissions API. This is what
    // distinguishes "user has explicitly denied" (clear actionable message)
    // from "permission state unknown" (call getCurrentPosition, which will
    // trigger the system prompt). Without this, the only signal we get is
    // an opaque POSITION_UNAVAILABLE that doesn't help the user act.
    const requestPosition = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lon: longitude });

          const myLocationArea: AreaPreset = {
            id: 'my-location',
            name: 'My Location',
            lat: latitude,
            lon: longitude,
            defaultRadiusKm: 50,
          };

          setSelectedArea(myLocationArea);
          setRecenterTrigger(t => t + 1);
          setIsLocating(false);
        },
        (error) => {
          setIsLocating(false);
          // Detect TWA via display-mode to give Android-specific instructions
          // when Settings is the right next step.
          const isAndroidApp = window.matchMedia('(display-mode: standalone)').matches
            || (navigator as Navigator & { standalone?: boolean }).standalone === true;
          switch (error.code) {
            case error.PERMISSION_DENIED:
              showToast({
                type: 'error',
                title: 'Location access blocked',
                message: isAndroidApp
                  ? 'Open Android Settings → Apps → Firewatch → Permissions → Location and choose "Allow only while using the app".'
                  : 'Allow location for this site in your browser settings, then try again.',
              });
              break;
            case error.POSITION_UNAVAILABLE:
              // In a TWA, POSITION_UNAVAILABLE usually means location services
              // are off device-wide, or the app lacks the system-level
              // permission entirely. The Settings path covers both.
              showToast({
                type: 'error',
                title: 'Location unavailable',
                message: isAndroidApp
                  ? 'Check that Location is turned on in Android Quick Settings, and that Firewatch has Location permission.'
                  : 'Your device couldn\'t determine your position right now. Try moving outdoors or near a window.',
              });
              break;
            case error.TIMEOUT:
              showToast({ type: 'error', title: 'Request timed out', message: 'Getting your location took too long. Try again in a moment.' });
              break;
            default:
              showToast({ type: 'error', title: 'Location error', message: 'Couldn\'t get your location. Try again in a moment.' });
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    };

    // Always call getCurrentPosition directly. Don't pre-flight via the
    // Permissions API: in a TWA, Chrome reports 'denied' whenever the
    // underlying Android app permission has never been granted — even on
    // the very first call. Short-circuiting on that state prevents the
    // locationdelegation activity from ever firing, which is exactly the
    // path that triggers the native Android permission dialog. Letting
    // getCurrentPosition fail with PERMISSION_DENIED is the correct
    // signal for "open Settings", and on first call it triggers the
    // prompt instead of failing.
    requestPosition();
  }, [showToast, userLocation]);

  /**
   * Handle event selection from list or map.
   * Centers the map and opens the detail panel.
   * PERFORMANCE: Memoized to prevent child re-renders.
   */
  const handleEventSelect = useCallback((event: UnifiedEvent, source: 'map' | 'list' | 'timeline' | 'notification' = 'map') => {
    trackFireEventSelect(
      event.id,
      event.title,
      (event.data?.alertLevel as string) || 'unknown',
      event.region || 'unknown',
      source
    );
    setSelectedEvent(event);

    // On mobile, auto-expand bottom sheet to show fire detail panel
    if (window.innerWidth <= 768 && mobileView === 'map') {
      setMobileView('list');
    }
  }, [mobileView]);

  /**
   * Handle notification click - look up event by ID and select it.
   * This zooms the map to the event and opens the detail panel.
   */
  const handleNotificationEventSelect = useCallback((eventId: string) => {
    const event = events.find((e) => e.id === eventId);
    if (event) {
      handleEventSelect(event, 'notification');
    }
  }, [events, handleEventSelect]);

  // Deep-link from a tapped notification. The Android TWA's LauncherActivity
  // appends `?event=<id>` to the launch URL when the user taps an FCM
  // notification (built from the watch_zone_processor's `url: /event/<id>`
  // payload). We pick that up here, find the event in the loaded list, and
  // select it — which pans the map and opens the detail panel.
  //
  // We wait for events to be present (first fetch), then strip the param
  // from the URL so a refresh doesn't keep re-deeplinking.
  const hasHandledDeepLink = useRef(false);
  useEffect(() => {
    if (hasHandledDeepLink.current) return;
    if (events.length === 0) return; // wait until events are loaded
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('event');
    if (!targetId) return;
    hasHandledDeepLink.current = true;
    handleNotificationEventSelect(targetId);
    // Clean URL so a manual refresh doesn't re-deeplink to the same event.
    const url = new URL(window.location.href);
    url.searchParams.delete('event');
    window.history.replaceState({}, '', url.toString());
  }, [events, handleNotificationEventSelect]);

  /**
   * Handle closing the detail panel.
   * PERFORMANCE: Memoized to prevent child re-renders.
   */
  const handleCloseDetail = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  /**
   * Handle "Watch this area" CTA from the fire detail panel.
   * Closes the detail panel and routes the user into the watch zone
   * editor. On mobile, expand the bottom sheet and switch the sidebar
   * to the zones tab; on desktop the WatchZoneManager lives inside the
   * sidebar and is always accessible.
   *
   * Pre-filling the zone centre on the fire's location is wired via the
   * existing window.__watchZoneEditorUpdateLocation global that the
   * WatchZoneManager registers when mounted. We push the location with
   * a short delay so the manager has time to mount on first visit.
   */
  const handleCreateWatchZone = useCallback((event: UnifiedEvent) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setSelectedEvent(null);
    if (window.innerWidth <= 768) {
      // Mobile: expand the bottom sheet so the watch zone manager has
      // somewhere to render. The MobileSidebar reads its forced view
      // from the requestedSidebarView state below.
      setMobileView('list');
      setRequestedSidebarView('zones');
      // Clear the request flag once MobileSidebar has had time to consume
      // it, so a subsequent manual nav back to the list view sticks.
      window.setTimeout(() => setRequestedSidebarView(null), 600);
    }
    // Pre-fill the zone centre with the fire's location after a delay
    // long enough for WatchZoneManager to mount and register its global.
    window.setTimeout(() => {
      const updateFn = (window as unknown as {
        __watchZoneEditorUpdateLocation?: (lat: number, lon: number) => void;
      }).__watchZoneEditorUpdateLocation;
      if (updateFn) {
        updateFn(event.location.lat, event.location.lon);
      }
    }, 400);
  }, [isAuthenticated]);

  /**
   * Handle "View in 3D" button from fire detail panel.
   * Enters focused 3D mode centered on the selected fire.
   */
  const handleView3D = useCallback((event: UnifiedEvent) => {
    enterFocused3DMode({
      id: event.id,
      lat: event.location.lat,
      lng: event.location.lon,
      title: event.title,
    });
    // On mobile, switch to map view so the 3D map is visible (close the detail panel/list)
    setMobileView('map');
  }, [enterFocused3DMode]);

  /**
   * Handle selecting a watch zone from the list.
   * Centers the map on the zone location.
   */
  const handleWatchZoneSelect = useCallback((zone: WatchZone) => {
    if (zone.geometry.type === 'circle' && zone.geometry.center) {
      const zoneArea: AreaPreset = {
        name: zone.name,
        lat: zone.geometry.center.lat,
        lon: zone.geometry.center.lon,
        defaultRadiusKm: zone.geometry.radiusKm || 25,
      };
      setSelectedArea(zoneArea);
    }
  }, []);

  /**
   * Handle map click when watch zone editor is open.
   * Sets the zone center to the clicked location.
   */
  const handleMapClickForZone = useCallback((lat: number, lon: number) => {
    // Call the editor's update function if available
    const updateFn = (window as unknown as { __watchZoneEditorUpdateLocation?: (lat: number, lon: number) => void }).__watchZoneEditorUpdateLocation;
    if (updateFn) {
      updateFn(lat, lon);
    }
  }, []);

  /**
   * Handle editor center change for map preview.
   */
  const handleEditorCenterChange = useCallback((center: { lat: number; lon: number }) => {
    setEditorPreviewCenter(center);
  }, []);

  /**
   * Handle editor radius change for map preview.
   */
  const handleEditorRadiusChange = useCallback((radiusKm: number) => {
    setEditorPreviewRadius(radiusKm);
  }, []);

  /**
   * Clear editor preview when editor closes.
   */
  const handleEditorClose = useCallback(() => {
    setEditorPreviewCenter(null);
    setIsDrawingZone(false);
  }, []);

  /**
   * Handle shape type change from zone editor.
   */
  const handleShapeTypeChange = useCallback((shapeType: 'circle' | 'polygon') => {
    setZoneShapeType(shapeType);
    // For circle, we use the preview circle; for polygon, we use drawing layer
    if (shapeType === 'polygon') {
      setEditorPreviewCenter(null);
    }
  }, []);

  /**
   * Handle drawing start for polygon zones.
   */
  const handleDrawingStart = useCallback(() => {
    setIsDrawingZone(true);
  }, []);

  /**
   * Handle zone drawn on map (from ZoneDrawingLayer).
   */
  const handleZoneDrawn = useCallback((zone: { type: 'circle' | 'polygon'; center?: { lat: number; lon: number }; radiusKm?: number; coordinates?: Array<{ lat: number; lon: number }> }) => {
    // Call the editor's update function
    const updateFn = (window as unknown as { __watchZoneEditorHandleDrawn?: (zone: { type: 'circle' | 'polygon'; center?: { lat: number; lon: number }; radiusKm?: number; coordinates?: Array<{ lat: number; lon: number }> }) => void }).__watchZoneEditorHandleDrawn;
    if (updateFn) {
      updateFn(zone);
    }
    setIsDrawingZone(false);
  }, []);

  /**
   * Toggle zone visibility on map.
   */
  const handleToggleZoneVisibility = useCallback((zoneId: string) => {
    setVisibleZoneIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) {
        newSet.delete(zoneId);
      } else {
        newSet.add(zoneId);
      }
      return newSet;
    });
  }, []);

  /**
   * Remove a deleted zone from visible zones.
   */
  const handleZoneDeleted = useCallback((zoneId: string) => {
    setVisibleZoneIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(zoneId);
      return newSet;
    });
  }, []);

  /**
   * Check if a file is a video based on its MIME type.
   */
  const isVideoFile = (file: File): boolean => {
    return file.type.startsWith('video/');
  };

  // Concurrent upload limiting - max 3 simultaneous uploads
  const MAX_CONCURRENT_UPLOADS = 3;
  const uploadQueueRef = useRef<Promise<void>[]>([]);

  /**
   * Process a single file upload with GPS extraction.
   */
  const processFileUpload = async (file: File): Promise<void> => {
    let lat: number;
    let lon: number;
    let altitude: number | undefined;
    let takenAt: string | undefined;

    if (isVideoFile(file)) {
      // Videos: Send to server with placeholder coords - server extracts GPS via ffprobe
      lat = selectedArea.lat;
      lon = selectedArea.lon;
    } else {
      // Lazy load exifr only when needed (saves ~300KB from initial bundle)
      const exifr = await import('exifr');
      // Extract GPS data from image EXIF
      const gps = await exifr.gps(file);
      const exif = await exifr.parse(file, {
        pick: ['GPSAltitude', 'DateTimeOriginal'],
      });

      if (!gps?.latitude || !gps?.longitude) {
        showToast({ type: 'warning', title: 'No GPS Data', message: `"${file.name}" has no location metadata. Photos must be geotagged.` });
        return;
      }

      lat = gps.latitude;
      lon = gps.longitude;
      altitude = exif?.GPSAltitude;
      takenAt = exif?.DateTimeOriginal?.toISOString?.() || (exif?.DateTimeOriginal ? String(exif.DateTimeOriginal) : undefined);
    }

    // Get auth token for upload
    const authToken = await getAccessToken();

    // Upload to backend
    await uploadPhoto({
      file,
      lat,
      lon,
      altitude,
      takenAt,
      authToken: authToken || undefined,
    });

    showToast({ type: 'success', title: 'Upload Successful', message: `"${file.name}" will appear on the map once approved by an admin.`, duration: 6000 });
  };

  /**
   * Handle photo/video file selection and extract GPS data from EXIF.
   * Videos without GPS metadata will use the current map center as location.
   * Limits concurrent uploads to prevent browser stalls.
   */
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Process files with concurrency limit
    for (const file of fileArray) {
      // Wait if we're at max concurrent uploads
      while (uploadQueueRef.current.length >= MAX_CONCURRENT_UPLOADS) {
        // Wait for at least one upload to complete
        await Promise.race(uploadQueueRef.current);
      }

      // Create upload promise and track it
      const uploadPromise = processFileUpload(file)
        .catch((err) => {
          console.error('Failed to process file:', err);
          showToast({ type: 'error', title: 'Upload Failed', message: `Failed to process "${file.name}". Please try another file.` });
        })
        .finally(() => {
          // Remove from queue when done
          const index = uploadQueueRef.current.indexOf(uploadPromise);
          if (index > -1) {
            uploadQueueRef.current.splice(index, 1);
          }
        });

      uploadQueueRef.current.push(uploadPromise);
    }

    // Wait for all remaining uploads to complete
    await Promise.all(uploadQueueRef.current);

    // Reset input so same file can be selected again
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  /**
   * Handle opening the removal request modal.
   * PERFORMANCE: Memoized to prevent child re-renders.
   */
  const handleRequestRemoval = useCallback((photoId: string, fileName: string) => {
    setRemovalRequest({ photoId, fileName });
  }, []);

  /**
   * Load existing photos from the backend on startup.
   */
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        const response = await fetchPhotos();
        // Note: S3 URLs are already absolute, API-served files need base URL prepended
        const loadedPhotos: GeoPhoto[] = response.map((p) => ({
          id: p.id,
          url: p.url.startsWith('http') ? p.url : `${API_BASE_URL}${p.url}`,
          fileName: p.fileName,
          lat: p.lat,
          lon: p.lon,
          altitude: p.altitude ?? undefined,
          takenAt: p.takenAt ?? undefined,
          addedAt: p.addedAt,
        }));
        setPhotos(loadedPhotos);
      } catch (err) {
        console.error('Failed to load photos:', err);
      }
    };

    if (!showDisclaimer) {
      loadPhotos();
    }
  }, [showDisclaimer]);

  /**
   * Handle sidebar resize.
   */
  const handleResizeStart = () => {
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      // Constrain width between 350px and 800px
      const constrainedWidth = Math.max(350, Math.min(800, newWidth));
      setSidebarWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <div className="app" style={{ userSelect: isResizing ? 'none' : 'auto' }}>
      {/* Disclaimer Modal - shown on first visit */}
      {showDisclaimer && (
        <DisclaimerModal onAccept={handleDisclaimerAccept} />
      )}

      {/* Onboarding Modal - shown after disclaimer for new users */}
      {showOnboarding && (
        <Suspense fallback={null}>
          <OnboardingModal onClose={handleOnboardingClose} />
        </Suspense>
      )}

      {/* Safety Banner - permanently dismissible (stored in localStorage) */}
      <SafetyBanner
        isVisible={showBanner}
        onDismiss={() => {
          dismissSafetyBanner();
          setShowBanner(false);
        }}
      />

      {/* Cookie / analytics consent — first-time visitors only.
          Default-deny is set inline in index.html before gtag config, so this
          is purely a UI prompt to grant consent (or confirm denial). */}
      <CookieBanner />

      {/* Offline indicator — shows a red banner at the top when the device
          loses network. Critical for fire safety: users need to know when
          their fire data may be stale rather than silently seeing old state. */}
      <OfflineBanner />

      {/* Header */}
      <header className="app-header">
        {/* Top row: Logo, title, and meta */}
        <div className="app-header-top">
          <h1 className="app-title">
            <img src="/logo.png" alt="Hipponics Firewatch" className="app-title-logo" />
            <span className="app-title-text">Hipponics Firewatch</span>
          </h1>

          <div className="app-header-meta">
            {lastUpdated && (
              <span className="last-updated">
                Updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              className="refresh-button"
              onClick={() => { trackDataRefresh(); loadEvents(true); }}
              disabled={isLoading}
              title="Refresh events"
              aria-label={isLoading ? 'Refreshing events' : 'Refresh events'}
            >
              {isLoading ? '...' : '↻'}
            </button>
            {showOnboardingPill && (
              <div className="onboarding-pill" role="status">
                <button
                  type="button"
                  className="onboarding-pill-btn"
                  onClick={handleOnboardingPillClick}
                  title="Take a 30-second tour of the app"
                  aria-label="Take a tour"
                >
                  <span className="onboarding-pill-icon" aria-hidden="true">
                    <CompassIcon size={16} />
                  </span>
                  <span className="onboarding-pill-text">Take a tour</span>
                </button>
                <button
                  type="button"
                  className="onboarding-pill-dismiss"
                  onClick={handleOnboardingPillDismiss}
                  aria-label="Dismiss tour pill"
                  title="Dismiss"
                >
                  ×
                </button>
              </div>
            )}
            <NotificationBadge onLoginClick={() => { trackModalOpen('auth'); setShowAuthModal(true); }} onViewAll={() => { trackModalOpen('notifications'); setShowNotificationPage(true); }} onEventSelect={handleNotificationEventSelect} />
            <UserMenu onLoginClick={() => { trackModalOpen('auth'); setShowAuthModal(true); }} onAdminPanelClick={() => { trackModalOpen('admin'); setShowAdminPanel(true); }} onProfileClick={() => { trackModalOpen('profile'); setShowProfileModal(true); }} />
          </div>
        </div>

        {/* Bottom row: Control buttons */}
        <div className="header-controls">
          {/* Back to 2D button - visible in 3D mode */}
          {viewMode === '3d' && (
            <button
              className="header-control-btn header-back-to-2d"
              onClick={() => {
                setViewMode('2d');
                exitFocused3DMode();
              }}
              title="Return to 2D map view"
            >
              <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              <span className="header-control-btn-label">Back to 2D</span>
            </button>
          )}

          {/* 3D Layers — entry point to MapSettings3D (the panel previously
              only reachable via the bottom-right gear FAB, which users
              missed). Opens the same settings sheet but from a header
              button that mirrors the 2D Layers dropdown pattern. */}
          {viewMode === '3d' && (
            <button
              className="header-control-btn"
              onClick={() => window.dispatchEvent(new CustomEvent('firewatch:toggle-3d-layers'))}
              title="3D map layers and settings"
            >
              <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
              <span className="header-control-btn-label">Layers</span>
            </button>
          )}

          {/* Layers Dropdown - 2D map layer toggles. Hidden in 3D mode
              entirely — 3D layers live in MapSettings3D and are opened
              via the 3D Layers header button above. */}
          {viewMode === '2d' && (
          <div className="header-control-dropdown">
            <button
              className={`header-control-btn ${showLayersMenu ? 'active' : ''}`}
              onClick={() => setShowLayersMenu(!showLayersMenu)}
              title="Toggle map layers"
            >
              <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
              <span className="header-control-btn-label">Layers</span>
            </button>

            {showLayersMenu && (
              <div className="header-control-dropdown-menu layers-menu">
                <div className="header-control-dropdown-header">
                  Map Layers
                </div>

                {/* ----- Fires & warnings ----- */}
                <div className="layers-menu-section-label">Fires &amp; warnings</div>

                {/* Fire Burn Areas (actual fire perimeters) */}
                <button
                  className={`layers-menu-item ${showFireBoundaries ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('fire_boundaries', showFireBoundaries, setShowFireBoundaries)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C13 5 15 7 15 10C15 12 14 13 12 14C14 13 16 12 16 9C16 7 15 5 12 2ZM8 14C8 11 10 9 12 7C10 9 9 11 9 14C9 16 10 18 12 18C10 18 8 16 8 14ZM12 22C8 22 5 19 5 15C5 12 7 9 10 7C8 10 7 12 7 15C7 18 9 20 12 20C15 20 17 18 17 15C17 12 16 10 14 7C17 9 19 12 19 15C19 19 16 22 12 22Z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Fire Boundaries</span>
                  <LayerInfoButton layerKey="fire_boundaries" />
                  <span className={`layers-menu-toggle ${showFireBoundaries ? 'on' : ''}`} />
                </button>

                {/* Warning Areas (affected zones from VIC) */}
                <button
                  className={`layers-menu-item ${showWarningAreas ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('warning_areas', showWarningAreas, setShowWarningAreas)}
                >
                  <span className="layers-menu-icon warning-area-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3,2">
                      <polygon points="12,2 22,20 2,20" />
                    </svg>
                  </span>
                  <span className="layers-menu-label">Warning Areas</span>
                  <LayerInfoButton layerKey="warning_areas" />
                  <span className={`layers-menu-toggle ${showWarningAreas ? 'on' : ''}`} />
                </button>

                {/* Hotspots */}
                <button
                  className={`layers-menu-item ${showHotspots ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('satellite_hotspots', showHotspots, setShowHotspots)}
                >
                  <span className="layers-menu-icon hotspot-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="12" r="4"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Satellite Hotspots</span>
                  <LayerInfoButton layerKey="hotspots" />
                  <span className={`layers-menu-toggle ${showHotspots ? 'on' : ''}`} />
                </button>

                {/* Planned Burns — on by default. Backend tags isPlannedBurn at
                    ingestion across NSW/VIC/QLD/SA high-confidence, WA text-match,
                    TAS/ACT best-effort. Lives in Fires & warnings (not Lightning &
                    storms — has nothing to do with lightning). */}
                <button
                  className={`layers-menu-item ${showPlannedBurns ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('planned_burns', showPlannedBurns, setShowPlannedBurns)}
                >
                  <span className="layers-menu-icon" style={{ color: '#9ca3af' }}>
                    {/* Dashed-border triangle to mirror the marker style */}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3,2">
                      <path d="M12 3 L22 21 L2 21 Z" />
                    </svg>
                  </span>
                  <span className="layers-menu-label">Planned burns</span>
                  <LayerInfoButton layerKey="planned_burns" />
                  <span className={`layers-menu-toggle ${showPlannedBurns ? 'on' : ''}`} />
                </button>

                {/* ----- Impact ----- */}
                <div className="layers-menu-divider" />
                <div className="layers-menu-section-label">Impact</div>

                {/* Road Closures */}
                <button
                  className={`layers-menu-item ${showRoadClosures ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('road_closures', showRoadClosures, setShowRoadClosures)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                      <line x1="12" y1="8" x2="12" y2="16"/>
                      <line x1="8" y1="12" x2="16" y2="12"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Road Closures</span>
                  <LayerInfoButton layerKey="road_closures" />
                  <span className={`layers-menu-toggle ${showRoadClosures ? 'on' : ''}`} />
                </button>

                {/* Power Outages */}
                <button
                  className={`layers-menu-item ${showPowerOutages ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('power_outages', showPowerOutages, setShowPowerOutages)}
                >
                  <span className="layers-menu-icon power-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Power Outages</span>
                  <LayerInfoButton layerKey="power_outages" />
                  <span className={`layers-menu-toggle ${showPowerOutages ? 'on' : ''}`} />
                </button>

                {/* Aircraft */}
                <button
                  className={`layers-menu-item ${showAircraftLayer ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('aircraft_tracking', showAircraftLayer, setShowAircraftLayer)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Aircraft Tracking</span>
                  <LayerInfoButton layerKey="aircraft" />
                  <span className={`layers-menu-toggle ${showAircraftLayer ? 'on' : ''}`} />
                </button>

                {/* Photos */}
                <button
                  className={`layers-menu-item ${showPhotosLayer ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('community_photos', showPhotosLayer, setShowPhotosLayer)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Community Photos</span>
                  <LayerInfoButton layerKey="photos" />
                  <span className={`layers-menu-toggle ${showPhotosLayer ? 'on' : ''}`} />
                </button>

                {/* ----- Conditions ----- */}
                <div className="layers-menu-divider" />
                <div className="layers-menu-section-label">Conditions</div>

                {/* Wind */}
                <button
                  className={`layers-menu-item ${showWindLayer ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('wind_animation', showWindLayer, setShowWindLayer)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Wind Animation</span>
                  <LayerInfoButton layerKey="wind" />
                  <span className={`layers-menu-toggle ${showWindLayer ? 'on' : ''}`} />
                </button>

                {/* Smoke */}
                <button
                  className={`layers-menu-item ${showSmoke ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('smoke_nasa', showSmoke, setShowSmoke)}
                >
                  <span className="layers-menu-icon smoke-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" opacity="0.7">
                      <circle cx="8" cy="10" r="4"/>
                      <circle cx="14" cy="8" r="3"/>
                      <circle cx="12" cy="14" r="3"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Smoke (NASA)</span>
                  <LayerInfoButton layerKey="smoke" />
                  <span className={`layers-menu-toggle ${showSmoke ? 'on' : ''}`} />
                </button>

                {/* Air Quality */}
                <button
                  className={`layers-menu-item ${showAirQuality ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('air_quality', showAirQuality, setShowAirQuality)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Air Quality</span>
                  <LayerInfoButton layerKey="air_quality" />
                  <span className={`layers-menu-toggle ${showAirQuality ? 'on' : ''}`} />
                </button>

                {/* City Temperatures */}
                <button
                  className={`layers-menu-item ${showCityTemperatures ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('city_temperatures', showCityTemperatures, setShowCityTemperatures)}
                >
                  <span className="layers-menu-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">City Temperatures</span>
                  <LayerInfoButton layerKey="city_temperatures" />
                  <span className={`layers-menu-toggle ${showCityTemperatures ? 'on' : ''}`} />
                </button>

                {/* ----- Lightning & storms ----- */}
                <div className="layers-menu-divider" />
                <div className="layers-menu-section-label">Lightning &amp; storms</div>

                {/* Thunderstorm Warnings */}
                <button
                  className={`layers-menu-item ${showThunderstormWarnings ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('thunderstorm_warnings', showThunderstormWarnings, setShowThunderstormWarnings)}
                >
                  <span className="layers-menu-icon" style={{ color: '#eab308' }}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17 10H20L13 21V13H9L11 6H7V3H17V10Z"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Thunderstorm Warnings</span>
                  <LayerInfoButton layerKey="thunderstorm_warnings" />
                  <span className={`layers-menu-toggle ${showThunderstormWarnings ? 'on' : ''}`} />
                </button>

                {/* Lightning Risk */}
                <button
                  className={`layers-menu-item ${showLightningRisk ? 'active' : ''}`}
                  onClick={createTrackedLayerToggle('dry_lightning_risk', showLightningRisk, setShowLightningRisk)}
                >
                  <span className="layers-menu-icon" style={{ color: '#f97316' }}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11 21h-1l1-7H7.5c-.88 0-.33-.75-.31-.78C8.48 10.94 10.42 7.54 13.01 3h1l-1 7h3.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z"/>
                      <circle cx="18" cy="5" r="3" fill="#dc2626" opacity="0.8"/>
                    </svg>
                  </span>
                  <span className="layers-menu-label">Dry Lightning Risk</span>
                  <LayerInfoButton layerKey="dry_lightning_risk" />
                  <span className={`layers-menu-toggle ${showLightningRisk ? 'on' : ''}`} />
                </button>

              </div>
            )}
          </div>
          )}

          {/* Add Photo Button - open to all users, admin approval required.
              Hidden in 3D mode: photo upload makes more sense from a 2D
              spatial context where the user is browsing locations, not
              while inspecting terrain. Keeps the 3D header compact. */}
          {viewMode === '2d' && (
            <button
              className="header-control-btn"
              onClick={() => setShowAddMediaModal(true)}
              title="Add geotagged photo or video (requires admin approval)"
            >
              <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span className="header-control-btn-label">Add Media</span>
            </button>
          )}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />

          {/* Radio Scanner Dropdown — 2D only. Audio context doesn't add
              value during terrain inspection or simulator ride-along, and
              keeps the 3D header from getting crowded. */}
          {viewMode === '2d' && (
          <div className="header-control-dropdown">
            <button
              className={`header-control-btn ${showRadioMenu ? 'active' : ''}`}
              onClick={() => setShowRadioMenu(!showRadioMenu)}
              title="Listen to NSW RFS radio scanners"
            >
              <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="2"/>
                <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/>
              </svg>
              <span className="header-control-btn-label">Radio</span>
            </button>

            {showRadioMenu && (
              <div className="header-control-dropdown-menu header-control-dropdown-menu-end">
                <div className="header-control-dropdown-header">
                  NSW RFS Scanner Feeds (Broadcastify)
                </div>
                {[
                  { name: 'Sydney Surrounds', url: 'https://www.broadcastify.com/listen/ctid/4350' },
                  { name: 'Mid North Coast RFS', url: 'https://www.broadcastify.com/listen/feed/33388' },
                  { name: 'Southern Tablelands', url: 'https://www.broadcastify.com/listen/feed/1176' },
                  { name: 'Central Coast', url: 'https://www.broadcastify.com/listen/ctid/4350' },
                ].map((feed) => (
                  <a
                    key={feed.name}
                    href={feed.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="header-control-dropdown-item"
                  >
                    {feed.name}
                    <span className="header-control-dropdown-external">↗</span>
                  </a>
                ))}
                <div className="header-control-dropdown-footer">
                  Opens in new tab
                </div>
              </div>
            )}
          </div>
          )}

          {/* About Button — hidden on mobile to reduce header-bar density.
              Mobile users reach About via UserMenu (top-right) or the
              footer links on /privacy.html and /terms.html. */}
          <button
            className="header-control-btn header-control-btn-desktop-only"
            onClick={() => { trackModalOpen('about'); setShowAboutModal(true); }}
            title="About this map and data sources"
          >
            <svg className="header-control-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <span className="header-control-btn-label">About</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className={`app-main ${mobileView === 'list' ? 'mobile-list-view' : 'mobile-map-view'}`}>
        {/* Map */}
        <div className="map-wrapper">
          {/* Floating Search Bar - visible on mobile without opening sidebar */}
          {/* Hidden in 3D focused mode since the focused header needs that space */}
          {!(viewMode === '3d' && focusedFire) && (
            <div className="map-search-overlay">
              <LocationSearch
                onLocationSelect={(location) => {
                  handleAreaChange(location);
                }}
                placeholder="Search location..."
              />
            </div>
          )}

          {/* 2D Map (Leaflet) */}
          {viewMode === '2d' && (
            <Map
              events={eventsForMap}
              center={{ lat: selectedArea.lat, lon: selectedArea.lon }}
              viewRadiusKm={selectedArea.defaultRadiusKm}
              recenterTrigger={recenterTrigger}
              isVisible={mobileView !== 'list'}
              onEventClick={handleEventSelect}
              onBoundsChange={setMapBounds}
              showWindLayer={showWindLayer}
              showAircraftLayer={showAircraftLayer}
              showFireBoundaries={showFireBoundaries}
              showWarningAreas={showWarningAreas}
              showHotspots={showHotspots}
              showRoadClosures={showRoadClosures}
              showPowerOutages={showPowerOutages}
              showSmoke={showSmoke}
              showAirQuality={showAirQuality}
              showCityTemperatures={showCityTemperatures}
              showThunderstormWarnings={showThunderstormWarnings}
              showLightningRisk={showLightningRisk}
              selectedEvent={selectedEvent}
              photos={showPhotosLayer ? photos : []}
              onRequestRemoval={handleRequestRemoval}
              onMyLocation={handleMyLocation}
              isLocating={isLocating}
              onMapClick={isEditorOpen && zoneShapeType === 'circle' ? handleMapClickForZone : undefined}
              editorPreviewCircle={editorPreviewCenter && zoneShapeType === 'circle' ? { center: editorPreviewCenter, radiusKm: editorPreviewRadius } : null}
              isDrawingZone={isDrawingZone && zoneShapeType === 'polygon'}
              zoneShapeType={zoneShapeType}
              onZoneDrawn={handleZoneDrawn}
              watchZones={zones}
              visibleZoneIds={visibleZoneIds}
              onZoneClick={handleWatchZoneSelect}
              onView3D={handleView3D}
              onRequestAuth={() => setShowAuthModal(true)}
              onDeselectEvent={handleCloseDetail}
              userLocation={userLocation}
              onAircraftRideAlong={(icao24) => {
                // Switch to 3D and seed Map3D with the icao24 to follow
                // in cockpit. Map3D's pendingAircraftFollow useEffect
                // does the actual handoff once Cesium is ready.
                setPendingAircraftFollow(icao24);
                setViewMode('3d');
              }}
            />
          )}

          {/* 3D Map (Cesium) - Lazy loaded with ErrorBoundary for graceful fallback */}
          {viewMode === '3d' && (
            <ErrorBoundary
              fallback={
                <div className="map-3d-error-fallback">
                  <div className="map-3d-error-content">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <h3>3D View Unavailable</h3>
                    <p>
                      The 3D terrain viewer encountered an error. This may be due to WebGL
                      compatibility or memory constraints on your device.
                    </p>
                    <button
                      className="map-3d-error-btn"
                      onClick={() => setViewMode('2d')}
                    >
                      Switch to 2D Map
                    </button>
                  </div>
                </div>
              }
            >
              <Suspense fallback={
                <div className="map-3d-loading">
                  <div className="map-3d-loading-spinner" />
                  <span>Loading 3D terrain...</span>
                </div>
              }>
                <Map3D
                  events={eventsForMap}
                  selectedEvent={selectedEvent}
                  onEventClick={handleEventSelect}
                  watchZones={zones}
                  visibleZoneIds={visibleZoneIds}
                  showFireBoundaries={showFireBoundaries}
                  showWarningAreas={showWarningAreas}
                  center={{ lat: selectedArea.lat, lon: selectedArea.lon }}
                  viewRadiusKm={selectedArea.defaultRadiusKm}
                  focusedFire={focusedFire}
                  userLocation={userLocation}
                  pendingAircraftFollow={pendingAircraftFollow}
                  onPendingAircraftFollowConsumed={() => setPendingAircraftFollow(null)}
                />
              </Suspense>
            </ErrorBoundary>
          )}

          {/* Mobile Zone Editor Instruction Bar */}
          {isEditorOpen && (
            <div className="mobile-zone-instruction-bar">
              <div className="mobile-zone-instruction-content">
                {zoneShapeType === 'circle' ? (
                  editorPreviewCenter ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span>Location set! Drag the map to adjust, or tap to move.</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                        <circle cx="12" cy="10" r="3" />
                        <path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" />
                      </svg>
                      <span>Tap on the map to set zone center</span>
                    </>
                  )
                ) : (
                  isDrawingZone ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                        <polygon points="12,2 22,20 2,20" />
                      </svg>
                      <span>Tap points to draw your zone. Double-tap to finish.</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      <span>Polygon drawn! Scroll down to complete setup.</span>
                    </>
                  )
                )}
              </div>
              <button
                className="mobile-zone-instruction-back"
                onClick={() => setMobileView('list')}
              >
                Continue
              </button>
            </div>
          )}
        </div>

        {/* Sidebar - acts as bottom sheet on mobile */}
        <div
          className={`sidebar-wrapper ${
            mobileView === 'list' ? 'sheet-expanded'
            : mobileView === 'half' ? 'sheet-half'
            : 'sheet-collapsed'
          }${landscapeRailCollapsed ? ' landscape-rail-collapsed' : ''}`}
          style={{ width: `${sidebarWidth}px` }}
          ref={bottomSheetRef}
        >
          {/* Landscape-only collapse tab. Sits on the left edge of the rail,
              peeks 22px into the map area when expanded; when collapsed the
              rail slides off-screen and only the tab remains visible at the
              right edge. CSS hides this in portrait + desktop. */}
          <button
            type="button"
            className="landscape-rail-toggle"
            aria-label={landscapeRailCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            onClick={() => setLandscapeRailCollapsed(prev => !prev)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points={landscapeRailCollapsed ? '15 18 9 12 15 6' : '9 18 15 12 9 6'} />
            </svg>
          </button>
          {/* Mobile bottom sheet drag handle - hidden on desktop.
              Three-column grid: chevron square / count+subtitle / severity chip.
              Severity color modifier (has-emergency) lifts to red top border + glow. */}
            <div
              className={`mobile-sheet-handle${sheetSummary.emergency > 0 ? ' has-emergency' : ''}`}
              onTouchStart={handleSheetTouchStart}
              onTouchMove={handleSheetTouchMove}
              onTouchEnd={handleSheetTouchEnd}
              onClick={() => {
                // Cycle: peek → half → list → peek. Matches Google Maps.
                const nextView: 'map' | 'half' | 'list' =
                  mobileView === 'map' ? 'half'
                  : mobileView === 'half' ? 'list'
                  : 'map';
                trackMobileViewToggle(nextView === 'list' ? 'list' : 'map');
                setMobileView(nextView);
                const sheet = bottomSheetRef.current;
                if (sheet) applySheetSnap(nextView, sheet);
              }}
              role="button"
              aria-label={
                mobileView === 'list' ? 'Collapse fire list'
                : mobileView === 'half' ? 'Expand fire list fully'
                : 'Open fire list'
              }
            >
              <div className="mobile-sheet-pill" />
              <span className="mobile-sheet-chevron" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {/* Up chevron in peek+half (more to reveal), down in list (close). */}
                  <polyline points={mobileView === 'list' ? '6 9 12 15 18 9' : '18 15 12 9 6 15'} />
                </svg>
              </span>
              <span className="mobile-sheet-label">
                {mobileView === 'list' ? (
                  <>
                    <span className="mobile-sheet-total">Showing fire list</span>
                    <span className="mobile-sheet-subtitle">Tap or drag down to close</span>
                  </>
                ) : sheetSummary.total > 0 ? (
                  <>
                    <span className="mobile-sheet-total">
                      {sheetSummary.total} {sheetSummary.total === 1 ? 'fire' : 'fires'} in view
                    </span>
                    <span className="mobile-sheet-subtitle">
                      {mobileView === 'half' ? 'Tap for full list' : 'Tap to peek at top fires'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="mobile-sheet-total mobile-sheet-total-empty">
                      No active fires in view
                    </span>
                    <span className="mobile-sheet-subtitle">Pan the map to find fires</span>
                  </>
                )}
              </span>
              <span className="mobile-sheet-badges" aria-hidden={sheetSummary.emergency === 0 && sheetSummary.watchAct === 0 ? 'true' : 'false'}>
                {/* Urgency chip — emergency wins, otherwise watch-and-act.
                    Hidden in the fully-expanded list state because the
                    emergency strip inside the list now carries that signal. */}
                {mobileView !== 'list' && sheetSummary.emergency > 0 ? (
                  <span className="mobile-sheet-pill-badge mobile-sheet-pill-emergency is-pulsing">
                    {sheetSummary.emergency} EMERGENCY
                  </span>
                ) : mobileView !== 'list' && sheetSummary.watchAct > 0 ? (
                  <span className="mobile-sheet-pill-badge mobile-sheet-pill-watch">
                    {sheetSummary.watchAct} watch &amp; act
                  </span>
                ) : null}
              </span>
            </div>
          {/* Resize Handle */}
          <div
            className="sidebar-resize-handle"
            onMouseDown={handleResizeStart}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              cursor: 'ew-resize',
              backgroundColor: isResizing ? '#3b82f6' : 'transparent',
              zIndex: 1000,
              transition: isResizing ? 'none' : 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isResizing) {
                e.currentTarget.style.backgroundColor = '#e5e7eb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isResizing) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          />
          {/* Official Links Panel — desktop only. On mobile this panel
              moves into the bottom-sheet footer (rendered by MobileSidebar)
              so the fire list isn't pushed below the fold by a desktop-style
              top panel with its own chrome. */}
          {!isMobileViewport && (
            <OfficialLinks
              isExpanded={isOfficialLinksOpen}
              onToggle={() => setIsOfficialLinksOpen(!isOfficialLinksOpen)}
            />
          )}

          {/* Main Sidebar — desktop and mobile use different layouts.
              The mobile version is a denser, one-glance fire view tuned
              for emergency / one-handed use. Desktop is unchanged. */}
          {isMobileViewport ? (
            <MobileSidebar
              events={visibleEvents}
              isLoading={isLoading}
              error={error}
              userLocation={userLocation}
              onEventSelect={handleEventSelect}
              onLoginClick={() => setShowAuthModal(true)}
              watchZoneCount={zones.length}
              selectedAreaName={selectedArea?.name}
              onWatchZoneSelect={handleWatchZoneSelect}
              onEditorClose={handleEditorClose}
              onEditorCenterChange={handleEditorCenterChange}
              onEditorRadiusChange={handleEditorRadiusChange}
              onShapeTypeChange={handleShapeTypeChange}
              onDrawingStart={handleDrawingStart}
              visibleZoneIds={visibleZoneIds}
              onToggleZoneVisibility={handleToggleZoneVisibility}
              onZoneDeleted={handleZoneDeleted}
              officialLinksOpen={isOfficialLinksOpen}
              onToggleOfficialLinks={() => setIsOfficialLinksOpen(!isOfficialLinksOpen)}
              requestedView={requestedSidebarView}
              onCreateWatchZoneEmptyState={() => {
                // Pattern #3: route the empty-state CTA through the same
                // handleCreateWatchZone path, but use the user's location
                // (or the selected map area) since there's no specific
                // fire to centre on.
                const fallbackLat = userLocation?.lat ?? selectedArea?.lat;
                const fallbackLon = userLocation?.lon ?? selectedArea?.lon;
                if (fallbackLat == null || fallbackLon == null) {
                  // No location available — fall through to sign-in / nav
                  // without prefill.
                  handleCreateWatchZone({
                    id: 'empty-state-cta',
                    type: 'FIRE_INCIDENT',
                    title: '',
                    location: { lat: 0, lon: 0 },
                    observedAt: new Date().toISOString(),
                    data: {},
                  } as unknown as UnifiedEvent);
                  return;
                }
                handleCreateWatchZone({
                  id: 'empty-state-cta',
                  type: 'FIRE_INCIDENT',
                  title: '',
                  location: { lat: fallbackLat, lon: fallbackLon },
                  observedAt: new Date().toISOString(),
                  data: {},
                } as unknown as UnifiedEvent);
              }}
            />
          ) : (
            <Sidebar
              events={visibleEvents}
              selectedArea={selectedArea}
              isLoading={isLoading}
              error={error}
              onAreaChange={handleAreaChange}
              onEventSelect={handleEventSelect}
              onLoginClick={() => setShowAuthModal(true)}
              onWatchZoneSelect={handleWatchZoneSelect}
              onEditorClose={handleEditorClose}
              onEditorCenterChange={handleEditorCenterChange}
              onEditorRadiusChange={handleEditorRadiusChange}
              onShapeTypeChange={handleShapeTypeChange}
              onDrawingStart={handleDrawingStart}
              visibleZoneIds={visibleZoneIds}
              onToggleZoneVisibility={handleToggleZoneVisibility}
              onZoneDeleted={handleZoneDeleted}
            />
          )}

          {/* Fire Detail Panel - overlays sidebar when event selected.
              Mobile gets a leaner layout; desktop keeps the wide-sidebar
              version with wind compass, social intel, etc.
              Lazy-loaded — chunk downloads on first fire selection. */}
          {selectedEvent && selectedEvent.type === 'FIRE_INCIDENT' && (
            <Suspense fallback={null}>
              {isMobileViewport ? (
                <MobileFireDetailPanel
                  event={selectedEvent}
                  onClose={handleCloseDetail}
                  onView3D={handleView3D}
                  onRequestAuth={() => setShowAuthModal(true)}
                  onCreateWatchZone={handleCreateWatchZone}
                  watchZoneCount={zones.length}
                />
              ) : (
                <FireDetailPanel
                  event={selectedEvent}
                  onClose={handleCloseDetail}
                  onView3D={handleView3D}
                  onRequestAuth={() => setShowAuthModal(true)}
                  onCreateWatchZone={handleCreateWatchZone}
                  watchZoneCount={zones.length}
                />
              )}
            </Suspense>
          )}

          {/* Health Detail Panel - overlays sidebar when health advisory selected */}
          {selectedEvent && selectedEvent.type === 'HEALTH' && (
            <HealthDetailPanel
              event={selectedEvent}
              onClose={handleCloseDetail}
            />
          )}
        </div>
      </main>

      {/* Lazy-loaded Modals - wrapped in Suspense for code splitting */}
      <Suspense fallback={null}>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => { trackModalClose('auth'); setShowAuthModal(false); }}
          />
        )}
        {showAdminPanel && (
          <AdminPanel
            isOpen={showAdminPanel}
            onClose={() => { trackModalClose('admin'); setShowAdminPanel(false); }}
          />
        )}
        {showProfileModal && (
          <ProfileModal
            isOpen={showProfileModal}
            onClose={() => { trackModalClose('profile'); setShowProfileModal(false); }}
          />
        )}
        {removalRequest !== null && (
          <RemovalRequestModal
            isOpen={removalRequest !== null}
            onClose={() => setRemovalRequest(null)}
            photoId={removalRequest?.photoId ?? ''}
            photoFileName={removalRequest?.fileName ?? ''}
          />
        )}
        {showAddMediaModal && (
          <AddMediaInfoModal
            isOpen={showAddMediaModal}
            onClose={() => setShowAddMediaModal(false)}
            onConfirm={() => photoInputRef.current?.click()}
          />
        )}
        {showAboutModal && (
          <AboutModal
            isOpen={showAboutModal}
            onClose={() => { trackModalClose('about'); setShowAboutModal(false); }}
            onStartTour={() => {
              trackModalClose('about');
              setShowAboutModal(false);
              trackModalOpen('onboarding');
              setShowOnboarding(true);
            }}
          />
        )}
        {showNotificationPage && (
          <NotificationPage
            isOpen={showNotificationPage}
            onClose={() => { trackModalClose('notifications'); setShowNotificationPage(false); }}
          />
        )}
        {showSupporterModal && (
          <SupporterModal
            onClose={() => { trackModalClose('supporter'); setShowSupporterModal(false); }}
          />
        )}
      </Suspense>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

/**
 * Main App component.
 * Wraps AppContent with MapViewProvider for 2D/3D view state management.
 * SupporterProvider manages subscription state for the supporter tier.
 */
export default function App() {
  return (
    <MapViewProvider>
      <SupporterProvider>
        <AppContent />
      </SupporterProvider>
    </MapViewProvider>
  );
}
